import { navigateTo } from '#imports'

import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'

import type {
  PaymentPostRedirection,
  PaymentRedirection,
} from '../types/PaymentRedirection'

/**
 * Use this composable to proceed to a payment redirection.
 *
 * @example
 *
 * const proceedRedirection = usePaymentRedirection()
 * ...
 * proceedRedirection(redirection)
 */
export const usePaymentRedirection = () => {
  const proceedPost = (redirection: PaymentPostRedirection) => {
    const { document } = window
    const form = document.createElement('form')
    try {
      form.method = 'POST'
      form.action = redirection.url

      Object.entries(redirection.data ?? {}).forEach(([name, value]) => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = name
        input.value = value
        form.appendChild(input)
      })

      document.body.appendChild(form)

      form.submit()
    } finally {
      form.remove()
    }
  }

  return (redirection: PaymentRedirection) => {
    switch (redirection.method) {
      case 'GET': {
        const nuxtLink = toNuxtLink(redirection.link)
        void navigateTo(nuxtLink, {
          // We consider that the link is external as soon as we don't navigate to a Nuxt named route
          external: !(typeof nuxtLink === 'object' && 'name' in nuxtLink),
        })

        return
      }
      case 'POST':
        proceedPost(redirection)

        return
      default:
        // @ts-expect-error API data may be invalid
        throw new Error(`Unknown redirection type: ${redirection.method}`)
    }
  }
}
